import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Button, Group, Stack, TextInput, Badge,
  ActionIcon, Modal, Checkbox, SimpleGrid, Loader, Center,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconEdit, IconTrash, IconShield } from '@tabler/icons-react'
import { getAccessLevels, createAccessLevel, updateAccessLevel, deleteAccessLevel } from '../../api/client'

const PAGE_GROUPS = [
  {
    label: '🏢 จัดการทรัพย์สิน (MAIN)',
    pages: [
      { key: 'module:dashboard-asset', label: 'แดชบอร์ดทรัพย์สิน (Dashboard Asset)' },
      { key: 'module:assets',      label: 'ทรัพย์สิน (Assets)' },
      { key: 'module:borrows',     label: 'การยืม (Borrow)' },
      { key: 'module:transfers',   label: 'โอนย้าย (Transfer)' },
      { key: 'module:handover',    label: 'ส่งมอบ (Handover)' },
      { key: 'module:audit',       label: 'ตรวจสอบอุปกรณ์' },
      { key: 'module:maintenance', label: 'ซ่อมบำรุง' },
      { key: 'module:licenses',    label: 'สัญญา & ใบอนุญาต' },
      { key: 'module:depreciation',label: 'ตัดจำหน่าย' },
    ],
  },
  {
    label: '🔧 งานบริการ (FACILITY)',
    pages: [
      { key: 'module:work-orders', label: 'งานแจ้งซ่อม (Work Order)' },
      { key: 'module:hk-orders',   label: 'คำขอของใช้ (HK Order)' },
    ],
  },
  {
    label: '📦 คลังและรายงาน (OPERATIONS)',
    pages: [
      { key: 'module:inventory',       label: 'คลังวัสดุ & Stock' },
      { key: 'report:asset-summary',   label: 'รายงานสรุปทรัพย์สิน' },
      { key: 'report:depreciation',    label: 'รายงานค่าเสื่อม' },
      { key: 'report:audit-log',       label: 'รายงาน Audit Log' },
      { key: 'report:maintenance',     label: 'รายงานซ่อมบำรุง' },
      { key: 'report:borrow',          label: 'รายงานการยืม' },
    ],
  },
  {
    label: '⚙️ Admin',
    pages: [
      { key: 'admin:users',         label: 'จัดการผู้ใช้งาน (Users)' },
      { key: 'admin:configuration', label: 'การตั้งค่าระบบ (Configuration)' },
      { key: 'module:changelog',    label: 'Change Log' },
      { key: 'report:audit-log',    label: 'Audit Log' },
    ],
  },
]

const EMPTY_FORM = { name: '', description: '', allowedPages: [] as string[] }

export default function AccessLevelTab() {
  const { t } = useTranslation()
  const [levels, setLevels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [editing, setEditing] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [opened, { open, close }] = useDisclosure(false)

  const load = async () => {
    setLoading(true)
    try { const { data } = await getAccessLevels(); setLevels(data) }
    catch {} finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM }); open() }
  const openEdit = (level: any) => {
    setEditing(level)
    let pages: string[] = []
    try { pages = JSON.parse(level.allowedPages) } catch {}
    setForm({ name: level.name, description: level.description ?? '', allowedPages: pages })
    open()
  }

  const togglePage = (key: string) =>
    setForm(p => ({
      ...p,
      allowedPages: p.allowedPages.includes(key)
        ? p.allowedPages.filter(k => k !== key)
        : [...p.allowedPages, key],
    }))

  const selectGroup = (pages: { key: string }[], all: boolean) => {
    const keys = pages.map(p => p.key)
    setForm(p => ({
      ...p,
      allowedPages: all
        ? [...new Set([...p.allowedPages, ...keys])]
        : p.allowedPages.filter(k => !keys.includes(k)),
    }))
  }

  const handleSave = async () => {
    if (!form.name.trim()) { notifications.show({ color: 'red', message: 'กรุณาใส่ชื่อ Access Level' }); return }
    setSaving(true)
    try {
      editing
        ? await updateAccessLevel(editing.id, { name: form.name, description: form.description, allowedPages: form.allowedPages })
        : await createAccessLevel({ name: form.name, description: form.description, allowedPages: form.allowedPages })
      notifications.show({ color: 'teal', message: t('common.success') })
      close(); load()
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setSaving(false) }
  }

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`ลบ "${name}" จริงไหม?`)) return
    try { await deleteAccessLevel(id); load() }
    catch { notifications.show({ color: 'red', message: t('common.error') }) }
  }

  const allPageKeys = PAGE_GROUPS.flatMap(g => g.pages)

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Box>
          <Text fw={600} mb={2}>Access Level</Text>
          <Text size="xs" c="dimmed">กำหนดว่า User แต่ละคนเข้าหน้าไหนได้บ้าง — สร้างได้ไม่จำกัด</Text>
        </Box>
        <Button leftSection={<IconPlus size={14} />} onClick={openCreate}
          style={{ background: 'var(--brand-primary,#0f172a)', color: 'var(--brand-on-primary,#fff)', border: '1px solid var(--brand-border,transparent)' }}>
          เพิ่ม Access Level
        </Button>
      </Group>

      <Box style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 14px', border: '1px solid #bfdbfe' }}>
        <Text size="xs" c="blue">💡 <b>ADMIN</b> เห็นทุกหน้าเสมอ ไม่ถูกจำกัดโดย Access Level — ใช้กับ ASSET_MANAGER, AUDITOR, USER</Text>
      </Box>

      {loading ? <Center py="xl"><Loader size="sm" /></Center>
        : levels.length === 0 ? (
          <Center py="xl"><Stack align="center" gap="xs">
            <IconShield size={40} color="#cbd5e1" />
            <Text size="sm" c="dimmed">ยังไม่มี Access Level</Text>
          </Stack></Center>
        ) : (
          <Stack gap="sm">
            {levels.map(level => {
              let pages: string[] = []
              try { pages = JSON.parse(level.allowedPages) } catch {}
              const pageLabels = allPageKeys.filter(p => pages.includes(p.key))
              return (
                <Box key={level.id} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 18px' }}>
                  <Group justify="space-between" mb={pageLabels.length ? 8 : 0}>
                    <Box>
                      <Text fw={600} size="sm">{level.name}</Text>
                      {level.description && <Text size="xs" c="dimmed">{level.description}</Text>}
                    </Box>
                    <Group gap={6}>
                      <Badge size="xs" variant="light" color="blue">{pages.length} หน้า</Badge>
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(level)}><IconEdit size={14} /></ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDelete(level.id, level.name)}><IconTrash size={14} /></ActionIcon>
                    </Group>
                  </Group>
                  {pageLabels.length > 0 && (
                    <Group gap={4} wrap="wrap">
                      {pageLabels.map(p => <Badge key={p.key} size="xs" variant="light" color="teal">{p.label}</Badge>)}
                    </Group>
                  )}
                </Box>
              )
            })}
          </Stack>
        )}

      <Modal opened={opened} onClose={close} size="lg" radius="lg"
        title={<Text fw={600} style={{ fontSize: 16 }}>{editing ? 'แก้ไข Access Level' : 'เพิ่ม Access Level'}</Text>}>
        <Stack gap="md">
          <SimpleGrid cols={2} spacing="sm">
            <TextInput label="ชื่อ *" placeholder="เช่น ช่างไฟฟ้า, แม่บ้าน"
              value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            <TextInput label="คำอธิบาย" placeholder="(ไม่บังคับ)"
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </SimpleGrid>

          <Text size="sm" fw={600}>หน้าที่เข้าถึงได้ ({form.allowedPages.length} หน้า)</Text>

          {PAGE_GROUPS.map(group => {
            const allSel = group.pages.every(p => form.allowedPages.includes(p.key))
            return (
              <Box key={group.label} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                <Group justify="space-between" mb={8}>
                  <Text size="xs" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>{group.label}</Text>
                  <Button size="xs" variant="subtle" color={allSel ? 'red' : 'teal'} onClick={() => selectGroup(group.pages, !allSel)}>
                    {allSel ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                  </Button>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={6}>
                  {group.pages.map(p => (
                    <Checkbox key={p.key} size="sm" label={p.label}
                      checked={form.allowedPages.includes(p.key)}
                      onChange={() => togglePage(p.key)} />
                  ))}
                </SimpleGrid>
              </Box>
            )
          })}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>ยกเลิก</Button>
            <Button loading={saving} onClick={handleSave}
              style={{ background: 'var(--brand-primary,#0f172a)', color: 'var(--brand-on-primary,#fff)', border: '1px solid var(--brand-border,transparent)' }}>
              {editing ? 'บันทึก' : 'สร้าง'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
